import { ApiDimension, ApiMetric, ApiProduct } from "@incendium/api";
import { TChartData } from "Interfaces";
import { formatPercentage } from "Helpers/percentage";
import {
  ProductAnalyticsBadge,
  ProductAnalyticsBadgeVariant,
  TopPerformingProduct,
} from "..";
import { smartSort } from "Helpers/arrays";

export const PRODUCT_REVENUE_GROWTH_FIELD = "revenueGrowthPercent";
export const PRODUCT_REVENUE_TREND_FIELD = "revenueTrend";

export const PRODUCT_CONVERSION_RATE_TOOLTIP =
  "Conversion is visits that led to a purchase. When a store first connects, we import past orders before visit data is available, so conversion can look very high until product pageviews start building up.";

const BADGE_CONFIG: Record<
  ProductAnalyticsBadgeVariant,
  { label: string; tooltip: string; color: string; bgColor: string }
> = {
  best_seller: {
    label: "Best Seller",
    tooltip: "The top-selling product in this view for your selected dates.",
    color: "#2E7D32",
    bgColor: "#E8F5E9",
  },
  high_margin: {
    label: "High Margin",
    tooltip:
      "This product earns a strong profit on each sale compared with others shown here.",
    color: "#E65100",
    bgColor: "#FFF3E0",
  },
  best_converting: {
    label: "Best Converting",
    tooltip:
      "Shoppers who view this product are especially likely to buy it. When a store first connects, we import past orders before visit data is available, so conversion can show as 100% until traffic builds up.",
    color: "#1565C0",
    bgColor: "#E3F2FD",
  },
  rising_star: {
    label: "Rising Star",
    tooltip:
      "Sales for this product are growing faster than others in your comparison period.",
    color: "#2E7D32",
    bgColor: "#E8F5E9",
  },
  low_stock: {
    label: "Low Stock",
    tooltip:
      "Stock for this product is running low and may need replenishing soon.",
    color: "#C62828",
    bgColor: "#FFEBEE",
  },
};

export function getBadgeConfig(variant: ProductAnalyticsBadgeVariant) {
  return BADGE_CONFIG[variant];
}

function productCompletenessScore(product: ApiProduct): number {
  const hasImage = !!product.imageUrl;
  const hasCategory = !!(product.productCategory?.name || product.category);
  return (hasImage ? 1 : 0) + (hasCategory ? 1 : 0);
}

export function findProductByName(
  products: ApiProduct[],
  name: string,
): ApiProduct | undefined {
  const normalizedName = name?.toLowerCase();
  const matches = products.filter(
    (p) => p.name?.toLowerCase() === normalizedName,
  );

  return smartSort(matches, [
    { getValue: productCompletenessScore, asc: false },
    { getValue: (p) => p.id ?? 0, asc: false },
  ])[0];
}

export function getProductMarginPercent(
  product?: ApiProduct,
): number | undefined {
  const margin = product?.profitMargins?.[0]?.percentage;
  return margin !== undefined ? Number(margin) : undefined;
}

export function getRowMarginPercent(row: TChartData): number | undefined {
  const value = row[ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT];
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
}

export function getProductCategoryLabel(product?: ApiProduct): string {
  return product?.productCategory?.name || product?.category || "Uncategorised";
}

export function getPrimaryMetricLabel(metric: ApiMetric): string {
  switch (metric) {
    case ApiMetric.METRIC_PRODUCT_GATEWAY_LTV:
      return "Gateway LTV";
    case ApiMetric.METRIC_RETURNING_CUSTOMER_RATE:
      return "Repeat Rate";
    case ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT:
      return "Margin";
    default:
      return "Revenue";
  }
}

export function getPrimaryMetricValue(product: TopPerformingProduct): number {
  switch (product.primaryMetric) {
    case ApiMetric.METRIC_PRODUCT_GATEWAY_LTV:
      return product.gatewayLtv || 0;
    case ApiMetric.METRIC_RETURNING_CUSTOMER_RATE:
      return product.returningCustomerRate || 0;
    case ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT:
      return product.marginPercent || 0;
    default:
      return product.revenue;
  }
}

export function revenueChangePercent(
  current: number,
  previous: number,
): number {
  if (!previous) {
    return current > 0 ? 100 : 0;
  }
  return ((current - previous) / previous) * 100;
}

export function formatRevenueGrowthPercent(value: number): string {
  return formatPercentage(value);
}

function getRowProductName(row: TChartData): string {
  return String(row[ApiDimension.DIMENSION_PRODUCT_NAME] || row.name || "");
}

function findComparisonRow(
  comparisonRows: TChartData[],
  name: string,
): TChartData | undefined {
  return comparisonRows.find((row) => getRowProductName(row) === name);
}

export function assignProductBadge(
  row: TChartData,
  index: number,
  rows: TChartData[],
  products: ApiProduct[],
  comparisonRows: TChartData[] = [],
): ProductAnalyticsBadge {
  const name = String(row.name || "");
  const margin = getRowMarginPercent(row);

  const margins = rows
    .map((r) => getRowMarginPercent(r))
    .filter((v): v is number => v !== undefined);
  const maxMargin = margins.length ? Math.max(...margins) : 0;

  const conversions = rows.map((r) =>
    Number(r[ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE] || 0),
  );
  const maxConversion = conversions.length ? Math.max(...conversions) : 0;
  const rowConversion = Number(
    row[ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE] || 0,
  );

  if (index === 0) {
    return { variant: "best_seller", label: BADGE_CONFIG.best_seller.label };
  }

  if (margin !== undefined && margin >= maxMargin && maxMargin > 0) {
    return { variant: "high_margin", label: BADGE_CONFIG.high_margin.label };
  }

  if (rowConversion >= maxConversion && maxConversion > 0) {
    return {
      variant: "best_converting",
      label: BADGE_CONFIG.best_converting.label,
    };
  }

  const compRow = findComparisonRow(comparisonRows, name);
  const currentRevenue = Number(row[ApiMetric.METRIC_PRODUCT_REVENUE] || 0);
  const previousRevenue = Number(
    compRow?.[ApiMetric.METRIC_PRODUCT_REVENUE] || 0,
  );
  const change = revenueChangePercent(currentRevenue, previousRevenue);

  const changes = rows.map((r) => {
    const comp = findComparisonRow(comparisonRows, getRowProductName(r));
    return revenueChangePercent(
      Number(r[ApiMetric.METRIC_PRODUCT_REVENUE] || 0),
      Number(comp?.[ApiMetric.METRIC_PRODUCT_REVENUE] || 0),
    );
  });
  const maxChange = changes.length ? Math.max(...changes) : 0;

  if (change >= maxChange && maxChange > 0) {
    return { variant: "rising_star", label: BADGE_CONFIG.rising_star.label };
  }

  return { variant: "rising_star", label: BADGE_CONFIG.rising_star.label };
}

export function buildTopPerformingProducts(
  rows: TChartData[],
  products: ApiProduct[],
  comparisonRows: TChartData[] = [],
  primaryMetric: ApiMetric = ApiMetric.METRIC_PRODUCT_REVENUE,
): TopPerformingProduct[] {
  return rows.map((row, index) => {
    const name = getRowProductName(row);
    const comparisonRevenue = Number(
      findComparisonRow(comparisonRows, name)?.[
        ApiMetric.METRIC_PRODUCT_REVENUE
      ] || 0,
    );
    const revenue = Number(row[ApiMetric.METRIC_PRODUCT_REVENUE] || 0);

    return {
      name,
      revenue,
      gatewayLtv:
        Number(row[ApiMetric.METRIC_PRODUCT_GATEWAY_LTV] || 0) || undefined,
      returningCustomerRate: Number(
        row[ApiMetric.METRIC_RETURNING_CUSTOMER_RATE] || 0,
      ),
      marginPercent: getRowMarginPercent(row),
      conversionRate: Number(row[ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE] || 0),
      pageviews: Number(row[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT] || 0),
      badge: assignProductBadge(row, index, rows, products, comparisonRows),
      comparisonRevenue,
      revenueGrowthPercent: revenueChangePercent(revenue, comparisonRevenue),
      primaryMetric,
    };
  });
}

export function mapTrendRowsToPoints(
  trendRows: TChartData[],
  metric: ApiMetric = ApiMetric.METRIC_PRODUCT_REVENUE,
): number[] {
  return trendRows
    .map((row) => ({
      date: String(
        row[ApiDimension.DIMENSION_SESSION_START_BY_DAY] ||
          row[ApiDimension.DIMENSION_SESSION_START_BY_WEEK] ||
          "",
      ),
      value: Number(row[metric] || 0),
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((point) => point.value);
}
